/**
 * ProductMaintenanceHub - Session summaries and work item tracking
 * Features: Session history, work items (features/bugs/improvements), priority management
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Wrench,
  CheckCircle2,
  Clock,
  AlertCircle,
  Bug,
  Sparkles,
  Zap,
  FileText,
  Calendar,
  ArrowRight,
  MoreVertical,
  Loader2,
  RefreshCw,
  Trash2,
  Edit2,
  ChevronRight,
  Target,
  Archive,
  Code
} from "lucide-react";

// Priority colors and labels
const PRIORITY_CONFIG = {
  P0: { color: "bg-red-100 text-red-800 border-red-200", label: "Critical", icon: AlertCircle },
  P1: { color: "bg-orange-100 text-orange-800 border-orange-200", label: "High", icon: Zap },
  P2: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "Medium", icon: Target },
  P3: { color: "bg-gray-100 text-gray-700 border-gray-200", label: "Low", icon: Archive }
};

// Item type config
const TYPE_CONFIG = {
  feature: { color: "bg-violet-100 text-violet-800", icon: Sparkles, label: "Feature" },
  bug: { color: "bg-red-100 text-red-800", icon: Bug, label: "Bug" },
  improvement: { color: "bg-blue-100 text-blue-800", icon: Zap, label: "Improvement" },
  debt: { color: "bg-amber-100 text-amber-800", icon: Code, label: "Tech Debt" }
};

// Status config
const STATUS_CONFIG = {
  backlog: { color: "bg-gray-100 text-gray-700", label: "Backlog" },
  planned: { color: "bg-blue-100 text-blue-800", label: "Planned" },
  in_progress: { color: "bg-yellow-100 text-yellow-800", label: "In Progress" },
  completed: { color: "bg-green-100 text-green-800", label: "Completed" },
  blocked: { color: "bg-red-100 text-red-800", label: "Blocked" }
};

export default function ProductMaintenanceHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  
  // Dialogs
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [newItem, setNewItem] = useState({
    title: "",
    description: "",
    item_type: "feature",
    priority: "P2",
    tags: []
  });
  const [newSession, setNewSession] = useState({
    title: "",
    completed_items: [],
    pending_items: [],
    notes: ""
  });
  
  useEffect(() => {
    fetchDashboard();
    fetchWorkItems();
    fetchSessions();
  }, [filterStatus, filterType, filterPriority]);
  
  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/maintenance-hub/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    }
  };
  
  const fetchWorkItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      let url = `${API}/maintenance-hub/work-items?limit=100`;
      if (filterStatus !== "all") url += `&status=${filterStatus}`;
      if (filterType !== "all") url += `&item_type=${filterType}`;
      if (filterPriority !== "all") url += `&priority=${filterPriority}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkItems(data.items || []);
        setStatusCounts(data.status_counts || {});
      }
    } catch (err) {
      console.error("Failed to fetch work items:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/maintenance-hub/sessions?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    }
  };
  
  const handleCreateItem = async () => {
    if (!newItem.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/maintenance-hub/work-items`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newItem)
      });
      
      if (res.ok) {
        toast.success("Work item created!");
        setShowAddItem(false);
        setNewItem({ title: "", description: "", item_type: "feature", priority: "P2", tags: [] });
        fetchWorkItems();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create item");
      }
    } catch (err) {
      toast.error("Failed to create item");
    }
  };
  
  const handleUpdateItem = async (itemId, updates) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/maintenance-hub/work-items/${itemId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        toast.success("Item updated!");
        fetchWorkItems();
        fetchDashboard();
        setEditingItem(null);
      }
    } catch (err) {
      toast.error("Failed to update item");
    }
  };
  
  const handleDeleteItem = async (itemId) => {
    if (!confirm("Delete this item?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/maintenance-hub/work-items/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Item deleted");
        fetchWorkItems();
        fetchDashboard();
      }
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };
  
  const handleCreateSession = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/maintenance-hub/sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSession)
      });
      
      if (res.ok) {
        toast.success("Session summary created!");
        setShowAddSession(false);
        setNewSession({ title: "", completed_items: [], pending_items: [], notes: "" });
        fetchSessions();
        fetchDashboard();
      }
    } catch (err) {
      toast.error("Failed to create session");
    }
  };
  
  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-violet-600" />
              Product Maintenance Hub
            </h1>
            <p className="text-gray-600">Track sessions, features, bugs, and improvements</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { fetchDashboard(); fetchWorkItems(); fetchSessions(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowAddItem(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Work Item
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="items" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Work Items
              {dashboard?.work_items?.total > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {dashboard.work_items.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sessions" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Sessions
            </TabsTrigger>
          </TabsList>
          
          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Stats Cards */}
              <Card className="border-violet-200 bg-violet-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">In Progress</p>
                      <p className="text-3xl font-bold text-violet-700">
                        {dashboard?.work_items?.in_progress || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-violet-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Completed This Month</p>
                      <p className="text-3xl font-bold text-green-700">
                        {dashboard?.work_items?.completed_this_month || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Blocked</p>
                      <p className="text-3xl font-bold text-red-700">
                        {dashboard?.work_items?.blocked || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Priority Items */}
              <div className="md:col-span-2">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Top Priority Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px] pr-4">
                      {dashboard?.top_priority_items?.length > 0 ? (
                        <div className="space-y-2">
                          {dashboard.top_priority_items.map((item) => {
                            const priorityConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.P2;
                            const typeConfig = TYPE_CONFIG[item.item_type] || TYPE_CONFIG.feature;
                            const TypeIcon = typeConfig.icon;
                            
                            return (
                              <div
                                key={item.item_id}
                                className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => { setActiveTab("items"); setEditingItem(item); }}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-lg ${typeConfig.color} flex items-center justify-center`}>
                                    <TypeIcon className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{item.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant="outline" className={`text-[10px] ${priorityConfig.color}`}>
                                        {item.priority}
                                      </Badge>
                                      <Badge variant="outline" className="text-[10px]">
                                        {STATUS_CONFIG[item.status]?.label || item.status}
                                      </Badge>
                                    </div>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">No pending work items</p>
                          <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddItem(true)}>
                            <Plus className="w-4 h-4 mr-1" />
                            Add First Item
                          </Button>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
              
              {/* Recent Sessions */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Recent Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_sessions?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.recent_sessions.map((session) => (
                        <div
                          key={session.session_id}
                          className="p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => setActiveTab("sessions")}
                        >
                          <p className="font-medium text-sm truncate">{session.title}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(session.date).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">No sessions yet</p>
                      <Button variant="link" size="sm" onClick={() => setShowAddSession(true)}>
                        Add Session Summary
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Work Items Tab */}
          <TabsContent value="items">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="backlog">Backlog</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="improvement">Improvement</SelectItem>
                  <SelectItem value="debt">Tech Debt</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="P0">P0 - Critical</SelectItem>
                  <SelectItem value="P1">P1 - High</SelectItem>
                  <SelectItem value="P2">P2 - Medium</SelectItem>
                  <SelectItem value="P3">P3 - Low</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Status counts */}
              <div className="flex items-center gap-2 ml-auto">
                {Object.entries(statusCounts).map(([status, count]) => (
                  count > 0 && (
                    <Badge
                      key={status}
                      variant="outline"
                      className={`cursor-pointer ${filterStatus === status ? 'ring-2 ring-violet-500' : ''}`}
                      onClick={() => setFilterStatus(filterStatus === status ? "all" : status)}
                    >
                      {STATUS_CONFIG[status]?.label}: {count}
                    </Badge>
                  )
                ))}
              </div>
            </div>
            
            {/* Items List */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : workItems.length > 0 ? (
                  <div className="divide-y">
                    {workItems.map((item) => {
                      const priorityConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.P2;
                      const typeConfig = TYPE_CONFIG[item.item_type] || TYPE_CONFIG.feature;
                      const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.backlog;
                      const TypeIcon = typeConfig.icon;
                      
                      return (
                        <div
                          key={item.item_id}
                          className="p-4 hover:bg-gray-50 transition-colors"
                          data-testid={`work-item-${item.item_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                              <TypeIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{item.title}</h4>
                                <Badge variant="outline" className={`text-xs ${priorityConfig.color}`}>
                                  {item.priority}
                                </Badge>
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className={`text-xs ${statusConfig.color}`}>
                                  {statusConfig.label}
                                </Badge>
                                <Badge variant="outline" className={`text-xs ${typeConfig.color}`}>
                                  {typeConfig.label}
                                </Badge>
                                {item.tags?.map(tag => (
                                  <Badge key={tag} variant="secondary" className="text-[10px]">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {/* Quick status change */}
                              <Select
                                value={item.status}
                                onValueChange={(newStatus) => handleUpdateItem(item.item_id, { status: newStatus })}
                              >
                                <SelectTrigger className="w-28 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="backlog">Backlog</SelectItem>
                                  <SelectItem value="planned">Planned</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="blocked">Blocked</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingItem(item)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteItem(item.item_id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No work items found</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddItem(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Work Item
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowAddSession(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Session Summary
              </Button>
            </div>
            
            <div className="space-y-4">
              {sessions.length > 0 ? (
                sessions.map((session) => (
                  <Card key={session.session_id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{session.title}</CardTitle>
                        <Badge variant="outline">
                          {new Date(session.date).toLocaleDateString()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {session.completed_items?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-green-700 mb-1">Completed</p>
                          <ul className="text-sm space-y-1">
                            {session.completed_items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <CheckCircle2 className="w-3 h-3 text-green-600" />
                                {typeof item === 'string' ? item : item.title}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {session.pending_items?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-amber-700 mb-1">Pending</p>
                          <ul className="text-sm space-y-1">
                            {session.pending_items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <Clock className="w-3 h-3 text-amber-600" />
                                {typeof item === 'string' ? item : item.title}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {session.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{session.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No session summaries yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddSession(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add First Session
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Add Work Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Work Item</DialogTitle>
            <DialogDescription>Create a new feature, bug, or improvement</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="Short description of the work item"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Detailed description, acceptance criteria, etc."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select
                  value={newItem.item_type}
                  onValueChange={(v) => setNewItem({ ...newItem, item_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">Feature</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="debt">Tech Debt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={newItem.priority}
                  onValueChange={(v) => setNewItem({ ...newItem, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P0">P0 - Critical</SelectItem>
                    <SelectItem value="P1">P1 - High</SelectItem>
                    <SelectItem value="P2">P2 - Medium</SelectItem>
                    <SelectItem value="P3">P3 - Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleCreateItem}>Create Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Session Dialog */}
      <Dialog open={showAddSession} onOpenChange={setShowAddSession}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Session Summary</DialogTitle>
            <DialogDescription>Record what was accomplished in this session</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Session Title *</Label>
              <Input
                value={newSession.title}
                onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                placeholder={`Session ${new Date().toLocaleDateString()}`}
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newSession.notes}
                onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                placeholder="Key accomplishments, decisions, blockers..."
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSession(false)}>Cancel</Button>
            <Button onClick={handleCreateSession}>Create Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Item Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Work Item</DialogTitle>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingItem.description || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={editingItem.item_type}
                    onValueChange={(v) => setEditingItem({ ...editingItem, item_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feature">Feature</SelectItem>
                      <SelectItem value="bug">Bug</SelectItem>
                      <SelectItem value="improvement">Improvement</SelectItem>
                      <SelectItem value="debt">Tech Debt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={editingItem.priority}
                    onValueChange={(v) => setEditingItem({ ...editingItem, priority: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P0">P0</SelectItem>
                      <SelectItem value="P1">P1</SelectItem>
                      <SelectItem value="P2">P2</SelectItem>
                      <SelectItem value="P3">P3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editingItem.status}
                    onValueChange={(v) => setEditingItem({ ...editingItem, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="backlog">Backlog</SelectItem>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editingItem.notes || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                  placeholder="Additional notes, blockers, etc."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)}>Cancel</Button>
            <Button onClick={() => handleUpdateItem(editingItem.item_id, editingItem)}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
